/**
 * A purchase slip as a printed page: what was bought, from whom, on which
 * day, at what rates, and whether it was paid or put on the supplier's
 * account. One copy on A4, for the store's file or to hand to the supplier.
 */
import { formatPKR, formatServerTime } from '@foodrun/shared';

import { Button } from '../../components/ui/Button.jsx';
import { packs } from './ItemPicker.jsx';

export function PurchaseSlipSheet({ slip, onClose }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Print purchase slip"
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-2xl bg-surface p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="max-h-[70vh] overflow-y-auto rounded-lg border border-rule bg-white p-4 text-black">
          <PurchaseSlip slip={slip} />
        </div>
        <div className="no-print mt-3 flex gap-2">
          <Button variant="secondary" size="lg" className="flex-1" onClick={onClose}>
            Close
          </Button>
          <Button size="lg" className="flex-1" onClick={() => window.print()}>
            Print
          </Button>
        </div>
      </div>
    </div>
  );
}

/** The day a slip is dated to, as people write it. */
const dayOf = (ymd) => {
  const [y, m, d] = String(ymd || '')
    .slice(0, 10)
    .split('-')
    .map(Number);
  return y
    ? new Date(y, m - 1, d).toLocaleDateString('en-PK', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '';
};

export function PurchaseSlip({ slip }) {
  const lines = slip.items ?? [];
  const status = slip.onCredit
    ? slip.creditLeft > 0
      ? `On credit · ${formatPKR(slip.creditLeft)} still owed`
      : 'On credit · settled'
    : 'Paid';
  return (
    <div className="statement">
      <div
        style={{
          fontFamily: 'ui-sans-serif, system-ui, sans-serif',
          color: '#000',
          padding: '4mm 0',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            borderBottom: '2px solid #000',
            paddingBottom: 4,
          }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>Food Run · Purchase slip</div>
            <div style={{ fontSize: 12 }}>
              {slip.grn_number} · bought on {dayOf(slip.received_date)} · entered{' '}
              {formatServerTime(slip.created_at)}
              {slip.entered_by ? ` by ${slip.entered_by}` : ''}
            </div>
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>{status}</div>
        </div>
        <div style={{ fontSize: 13, margin: '6px 0' }}>
          Supplier: <strong>{slip.supplier_name}</strong>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid #000' }}>
              <th style={{ padding: '3px 0' }}>#</th>
              <th style={{ padding: '3px 0' }}>Item</th>
              <th style={{ padding: '3px 0', textAlign: 'right' }}>Packs</th>
              <th style={{ padding: '3px 0', textAlign: 'right' }}>Rate / pack</th>
              <th style={{ padding: '3px 0', textAlign: 'right' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={l.ingredientId ?? i} style={{ borderBottom: '1px solid #bbb' }}>
                <td style={{ padding: '3px 0' }}>{i + 1}</td>
                <td style={{ padding: '3px 0' }}>{l.name}</td>
                <td style={{ padding: '3px 0', textAlign: 'right' }}>
                  {packs(l.packs)} × {l.packName}
                </td>
                <td style={{ padding: '3px 0', textAlign: 'right' }}>{formatPKR(l.ratePerPack)}</td>
                <td style={{ padding: '3px 0', textAlign: 'right' }}>{formatPKR(l.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4} style={{ padding: '6px 0', textAlign: 'right', fontWeight: 700 }}>
                Total
              </td>
              <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 700 }}>
                {formatPKR(slip.total)}
              </td>
            </tr>
          </tfoot>
        </table>
        {slip.notes && <div style={{ fontSize: 12, marginTop: 6 }}>Note: {slip.notes}</div>}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 24,
            marginTop: 28,
            fontSize: 12,
          }}
        >
          <div style={{ flex: 1, borderTop: '1px solid #000', paddingTop: 3 }}>Store keeper</div>
          <div style={{ flex: 1, borderTop: '1px solid #000', paddingTop: 3 }}>Supplier</div>
        </div>
      </div>
    </div>
  );
}
